import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import type { ImportPaygapPersonnelDto } from "@green0meter/api-client";
import { UserRole } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import type { EmployeeData } from "../../hooks";
import { dateFieldToNumber } from "../../hooks/useEmployeeDataMapper";
import { getColumnDefinitions } from "./helpers";
import {
  useEmployeeEditing,
  useEmployeeTableState,
  validateCellValue,
} from "./hooks";
import {
  ColumnFilterMenu,
  ColumnSettingsDialog,
  DeleteConfirmDialog,
  EmployeeTable,
  FetchConfirmDialog,
  FilterChips,
  HIDEABLE_COLUMNS,
  MassEditToolbar,
  SuccessDialog,
  TablePagination,
  UpdateConfirmDialog,
} from "./components";

import { useUserStore } from "@store/user";
import { useGetCurrentCompanyPersonnel, useImportPersonnel } from "@api";

export type { EmployeeData } from "../../hooks";

export interface TableFilter {
  id: string;
  columnKey: string;
  value: string;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  group?: string;
  isGroupGeneral?: boolean;
}

export interface EditingCell {
  rowIndex: number;
  colKey: string;
  rowKey: string;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

interface EmployeeDataDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onDataFetched?: () => void;
  autoFetch?: boolean;
}

export const EmployeeDataDrawer: React.FC<EmployeeDataDrawerProps> = ({
  isOpen,
  onClose,
  onDataFetched,
  autoFetch = false,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    personnelData,
    refetchGetCurrentCompanyPersonnel,
    isLoadingGetCurrentCompanyPersonnel,
    getCurrentCompanyPersonnelError,
  } = useGetCurrentCompanyPersonnel(user, false);

  const { importPersonnel, isPendingImportPersonnel } = useImportPersonnel(user);

  // Local state
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [localPersonnelData, setLocalPersonnelData] = useState<EmployeeData[]>([]);
  const [validationError, setValidationError] = useState<string>("");
  const [hasManuallyAddedRows, setHasManuallyAddedRows] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dialog state
  const [showFetchConfirmation, setShowFetchConfirmation] = useState(false);
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<{ key: string } | null>(
    null,
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Column visibility state
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem("paygap-employee-hidden-columns");

    if (saved) {
      try {
        return new Set(JSON.parse(saved) as string[]);
      } catch {
        return new Set<string>();
      }
    }

return new Set<string>();
  });

  // Group expand/collapse state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const handleToggleGroup = useCallback((groupId: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);

      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  }, []);

  // Mass edit state
  const [massEditField, setMassEditField] = useState<string>("gender");
  const [massEditValue, setMassEditValue] = useState<string>("");

  // Memoized columns
  const columns = useMemo(() => getColumnDefinitions(t), [t]);

  // Filtered columns (exclude hidden ones)
  const visibleColumns = useMemo(
    () => columns.filter((col) => !hiddenColumns.has(col.key)),
    [columns, hiddenColumns],
  );

  // Column visibility handlers
  const handleToggleColumn = useCallback((columnKey: string): void => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);

      if (next.has(columnKey)) {
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      localStorage.setItem("paygap-employee-hidden-columns", JSON.stringify([...next]));

return next;
    });
  }, []);

  const handleShowAllColumns = useCallback((): void => {
    setHiddenColumns(new Set());
    localStorage.setItem("paygap-employee-hidden-columns", JSON.stringify([]));
  }, []);

  const handleHideAllColumns = useCallback((): void => {
    const allHideable = new Set(HIDEABLE_COLUMNS as unknown as string[]);

    setHiddenColumns(allHideable);
    localStorage.setItem("paygap-employee-hidden-columns", JSON.stringify([...allHideable]));
  }, []);

  // Custom hooks
  const tableState = useEmployeeTableState({
    localPersonnelData,
    columns,
  });

  const editing = useEmployeeEditing({
    localPersonnelData,
    setLocalPersonnelData,
    setValidationError,
  });

  // Sync personnel data from API (skip during save to prevent UI flickering)
  useEffect(() => {
    if (!isSaving && personnelData && personnelData.length > 0) {
      setLocalPersonnelData(personnelData as EmployeeData[]);
    }
  }, [personnelData, isSaving]);

  // Auto-fetch on drawer open
  useEffect(() => {
    if (isOpen && autoFetch === true && !isDataFetched) {
      handleFetchDataConfirm();
    }
  }, [isOpen, autoFetch]);

  // Fetch handlers
  const handleFetchData = useCallback((): void => {
    if (hasManuallyAddedRows && localPersonnelData.length > 0) {
      setShowFetchConfirmation(true);
    } else {
      handleFetchDataConfirm();
    }
  }, [hasManuallyAddedRows, localPersonnelData.length]);

  const handleFetchDataConfirm = useCallback((): void => {
    setIsDataFetched(false);
    tableState.setPage(0);
    refetchGetCurrentCompanyPersonnel();

    setTimeout((): void => {
      if (personnelData) {
        setIsDataFetched(true);
        setHasManuallyAddedRows(false);
        if (onDataFetched) {
          onDataFetched();
        }
      }
    }, 1000);

    setShowFetchConfirmation(false);
  }, [personnelData, refetchGetCurrentCompanyPersonnel, onDataFetched, tableState]);

  // Delete handlers
  const handleDeleteEmployee = useCallback(
    (rowKey: string): void => {
      const employee = localPersonnelData.find(
        (item) => String(item.key) === String(rowKey),
      );

      if (employee) {
        setEmployeeToDelete({ key: String(employee.key) });
        setShowDeleteConfirmation(true);
      }
    },
    [localPersonnelData],
  );

  const handleDeleteConfirm = useCallback((): void => {
    if (employeeToDelete) {
      setLocalPersonnelData((prev) =>
        prev.filter((employee) => String(employee.key) !== employeeToDelete.key),
      );
    }
    setShowDeleteConfirmation(false);
    setEmployeeToDelete(null);
  }, [employeeToDelete]);

  // Add employee handler
  const handleAddEmployee = useCallback((): void => {
    const nextKey =
      localPersonnelData.length === 0
        ? "1"
        : String(
            Math.max(...localPersonnelData.map((emp) => Number(emp.key) || 0)) + 1,
          );

    const newEmployee: EmployeeData = {
      key: nextKey,
      age: "",
      gender: "",
      yearsOfService: "",
      educationAndTraining: "",
      employeeScarcity: "",
      employeeEvaluation: "",
      category: "",
      function: "",
      levelOfRequirements: "",
      levelOfRequirements2: "",
      levelOfRequirements3: "",
      professionalPosition: "",
      activityRatePercent: "",
      paidHours: "",
      basicWage: "",
      wageCompensationForVacation: "",
      wageCompensationDoctorFamilyLeave: "",
      sickLeaveIncomeCompensation: "",
      allowances: "",
      overtimeWage: "",
      overtimeAllowance: "",
      riskAllowance: "",
      standbyDuty: "",
      thirteenthMonthlyWage: "",
      monthlyBonus: "",
      standardBonus: "",
      specialBonus: "",
      fiftyFivePlusBonus: "",
      retirementBenefit: "",
      severancePay: "",
      specialPayments: "",
      supplementaryPensionInsurance: "",
      lifeInsurance: "",
      mealVouchers: "",
      cafeteria: "",
      carAllowance: "",
      employeeContribution: "",
      usualWeeklyWorkingHours: "",
      usualAnnualWorkingHours: "",
      statisticalPopulation: "",
      remarks: "",
      additionalInformation01: "",
      additionalInformation02: "",
      additionalInformation03: "",
      additionalInformation04: "",
      additionalInformation05: "",
    };

    setLocalPersonnelData([...localPersonnelData, newEmployee]);
    setHasManuallyAddedRows(true);
    const totalPages = Math.ceil(
      (localPersonnelData.length + 1) / tableState.rowsPerPage,
    );

    tableState.setPage(totalPages - 1);
  }, [localPersonnelData, tableState]);

  // Mass edit handler
  const handleApplyMassEdit = useCallback((): void => {
    if (!massEditField) return;

    const targets =
      tableState.selectedKeys.size > 0
        ? new Set(tableState.selectedKeys)
        : new Set(tableState.filteredData.map((row) => String(row.key)));

    if (targets.size === 0) {
      setValidationError(t("paygap.employeeData.validation.noRows"));

return;
    }

    const validation = validateCellValue(t, massEditField, massEditValue);

    if (!validation.isValid) {
      setValidationError(validation.errorMessage);

return;
    }

    setLocalPersonnelData((prev) =>
      prev.map((employee) =>
        targets.has(String(employee.key))
          ? { ...employee, [massEditField]: massEditValue }
          : employee,
      ),
    );
    setValidationError("");
  }, [
    massEditField,
    massEditValue,
    tableState.selectedKeys,
    tableState.filteredData,
  ]);

  // Update database handlers
  const handleUpdateDatabase = useCallback((): void => {
    setShowUpdateConfirmation(true);
  }, []);

  const handleUpdateDatabaseConfirm = useCallback((): void => {
    setShowUpdateConfirmation(false);

    const mapPersonnelItem = (
      e: EmployeeData,
    ): ImportPaygapPersonnelDto["personnel"][number] =>
      ({
        key: e.key,
        gender: Number(e.gender) || 0,
        age: dateFieldToNumber(e.age),
        yearsOfService: dateFieldToNumber(e.yearsOfService),
        educationAndTraining: String(e.educationAndTraining ?? ""),
        employeeScarcity: Number(e.employeeScarcity) || 0,
        employeeEvaluation: Number(e.employeeEvaluation) || 0,
        category: String(e.category ?? ""),
        function: String(e.function),
        levelOfRequirements: e.levelOfRequirements || 0,
        levelOfRequirements2: e.levelOfRequirements2 || 0,
        levelOfRequirements3: e.levelOfRequirements3 || 0,
        professionalPosition: String(e.professionalPosition ?? ""),
        activityRatePercent: Number(e.activityRatePercent) || 0,
        paidHours: Number(e.paidHours) || 0,
        basicWage: Number(e.basicWage) || 0,
        wageCompensationForVacation: Number(e.wageCompensationForVacation) || 0,
        wageCompensationDoctorFamilyLeave: Number(e.wageCompensationDoctorFamilyLeave) || 0,
        sickLeaveIncomeCompensation: Number(e.sickLeaveIncomeCompensation) || 0,
        allowances: Number(e.allowances) || 0,
        overtimeWage: Number(e.overtimeWage) || 0,
        overtimeAllowance: Number(e.overtimeAllowance) || 0,
        riskAllowance: Number(e.riskAllowance) || 0,
        standbyDuty: Number(e.standbyDuty) || 0,
        thirteenthMonthlyWage: Number(e.thirteenthMonthlyWage) || 0,
        monthlyBonus: Number(e.monthlyBonus) || 0,
        standardBonus: Number(e.standardBonus) || 0,
        specialBonus: Number(e.specialBonus) || 0,
        fiftyFivePlusBonus: Number(e.fiftyFivePlusBonus) || 0,
        retirementBenefit: Number(e.retirementBenefit) || 0,
        severancePay: Number(e.severancePay) || 0,
        specialPayments: Number(e.specialPayments) || 0,
        supplementaryPensionInsurance: Number(e.supplementaryPensionInsurance) || 0,
        lifeInsurance: Number(e.lifeInsurance) || 0,
        mealVouchers: Number(e.mealVouchers) || 0,
        cafeteria: Number(e.cafeteria) || 0,
        carAllowance: Number(e.carAllowance) || 0,
        usualWeeklyWorkingHours: Number(e.usualWeeklyWorkingHours) || 0,
        usualAnnualWorkingHours: Number(e.usualAnnualWorkingHours) || 0,
        statisticalPopulation: Number(e.statisticalPopulation) || 0,
        remarks: String(e.remarks ?? ""),
        additionalInformation01: String(e.additionalInformation01 ?? ""),
        additionalInformation02: String(e.additionalInformation02 ?? ""),
        additionalInformation03: String(e.additionalInformation03 ?? ""),
        additionalInformation04: String(e.additionalInformation04 ?? ""),
        additionalInformation05: String(e.additionalInformation05 ?? ""),
      }) as unknown as ImportPaygapPersonnelDto["personnel"][number];

    const BATCH_SIZE = 1000;
    const batches: EmployeeData[][] = [];

    for (let i = 0; i < localPersonnelData.length; i += BATCH_SIZE) {
      batches.push(localPersonnelData.slice(i, i + BATCH_SIZE));
    }

    const processBatches = async (): Promise<void> => {
      setIsSaving(true);
      try {
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          const isLastBatch = i === batches.length - 1;

          const importPayload: ImportPaygapPersonnelDto = {
            personnel: batch.map(
              mapPersonnelItem,
            ) as unknown as ImportPaygapPersonnelDto["personnel"],
            overwrite: i === 0,
          };

          await new Promise<void>((resolve, reject) => {
            importPersonnel(importPayload, {
              onSuccess: () => resolve(),
              onError: (error: unknown) => reject(error),
            });
          });

          if (!isLastBatch) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        setValidationError("");
        setShowUpdateSuccess(true);
      } catch (error) {
        setValidationError(
          `${t("paygap.employeeData.error.updateFailed", "Failed to update database:")} ${error instanceof Error ? error.message : t("paygap.common.unknownError", "Unknown error")}`,
        );
      } finally {
        setIsSaving(false);
      }
    };

    processBatches();
  }, [localPersonnelData, importPersonnel, tableState, editing]);

  // Cancel handler
  const handleCancel = useCallback((): void => {
    setLocalPersonnelData([]);
    setIsDataFetched(false);
    tableState.setPage(0);
    editing.resetEditing();
    setValidationError("");
    onClose();
  }, [onClose, tableState, editing]);

  // Check if user is an Owner
  const isOwner = useMemo(() => user?.role === UserRole.Owner, [user?.role]);

  // Export all employee data to Excel (owner-only)
  const handleExportAllData = useCallback((): void => {
    if (localPersonnelData.length === 0) return;

    // Create headers from column definitions
    const headers = columns.map((col) => col.label);

    // Create data rows
    const rows = localPersonnelData.map((employee) =>
      columns.map((col) => employee[col.key as keyof EmployeeData] ?? ""),
    );

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      t("paygap.employeeData.excelSheetName", "Employee Data"),
    );

    // Export
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `employee_data_${new Date().toISOString().split("T")[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [localPersonnelData, columns, t]);

  return (
    <Drawer open={isOpen} anchor="right" onClose={onClose}>
      <Box
        width="90vw"
        maxWidth={1200}
        p={3}
        sx={{
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">
            {t("paygap.employeeData.drawer.title")}
          </Typography>
          <IconButton onClick={onClose}>
            <FeatherIcon icon="x" size={20} />
          </IconButton>
        </Box>

        <Typography variant="body1" mb={3}>
          {t("paygap.employeeData.drawer.description")}
        </Typography>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} mb={3}>
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={isLoadingGetCurrentCompanyPersonnel}
            startIcon={
              isLoadingGetCurrentCompanyPersonnel ? (
                <CircularProgress size={16} />
              ) : (
                <FeatherIcon icon="download" size={16} />
              )
            }
          >
            {isLoadingGetCurrentCompanyPersonnel
              ? t("paygap.employeeData.button.fetching")
              : t("paygap.employeeData.button.fetchData")}
          </Button>

          <Button
            variant="outlined"
            onClick={handleUpdateDatabase}
            disabled={isSaving || localPersonnelData.length === 0 || !isDataFetched}
            startIcon={
              isSaving ? (
                <CircularProgress size={16} />
              ) : (
                <FeatherIcon icon="upload" size={16} />
              )
            }
          >
            {isSaving
              ? t("paygap.employeeData.button.updating")
              : t("paygap.employeeData.button.updateDatabase")}
          </Button>

          <Button
            variant="outlined"
            onClick={handleAddEmployee}
            startIcon={<FeatherIcon icon="plus" size={16} />}
          >
            {t("paygap.employeeData.button.addEmployee")}
          </Button>

          <Button
            variant="outlined"
            onClick={(): void => setShowColumnSettings(true)}
            startIcon={<FeatherIcon icon="settings" size={16} />}
          >
            {t("paygap.employeeData.button.columnSettings", "Settings")}
          </Button>

          {isOwner && (
            <Button
              variant="outlined"
              onClick={handleExportAllData}
              disabled={localPersonnelData.length === 0}
              startIcon={<FeatherIcon icon="download" size={16} />}
            >
              {t("paygap.employeeData.button.exportAllData", "Export All Data")}
            </Button>
          )}
        </Stack>

        {/* Error Display */}
        {getCurrentCompanyPersonnelError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("paygap.employeeData.error.fetchingData")}{" "}
            {getCurrentCompanyPersonnelError.message}
          </Alert>
        )}

        {/* Data Display */}
        {((isDataFetched && personnelData) || localPersonnelData.length > 0) && (
          <Paper
            elevation={1}
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {validationError && (
              <Alert severity="error" sx={{ m: 2, mb: 1 }}>
                {validationError}
              </Alert>
            )}

            <FilterChips
              filters={tableState.filters}
              columns={columns}
              onRemoveFilter={tableState.removeFilter}
              onClearFilters={tableState.clearFilters}
            />

            <MassEditToolbar
              columns={columns}
              massEditField={massEditField}
              setMassEditField={setMassEditField}
              massEditValue={massEditValue}
              setMassEditValue={setMassEditValue}
              selectedCount={tableState.selectedKeys.size}
              filteredCount={tableState.filteredData.length}
              onApply={handleApplyMassEdit}
            />

            <EmployeeTable
              columns={visibleColumns}
              paginatedData={tableState.paginatedData}
              filters={tableState.filters}
              editingCell={editing.editingCell}
              editValue={editing.editValue}
              setEditValue={editing.setEditValue}
              selectedKeys={tableState.selectedKeys}
              allVisibleSelected={tableState.allVisibleSelected}
              someVisibleSelected={tableState.someVisibleSelected}
              expandedGroups={expandedGroups}
              onToggleGroup={handleToggleGroup}
              onCellClick={editing.handleCellClick}
              onSaveCell={editing.handleSaveCell}
              onKeyPress={editing.handleKeyPress}
              onToggleRowSelection={tableState.toggleRowSelection}
              onToggleSelectAllVisible={tableState.toggleSelectAllVisible}
              onOpenColumnFilterMenu={tableState.openColumnFilterMenu}
              onDeleteEmployee={handleDeleteEmployee}
            />

            <ColumnFilterMenu
              anchorEl={tableState.filterMenuAnchorEl}
              columnKey={tableState.filterMenuColumnKey}
              columns={columns}
              filterValue={tableState.filterMenuValue}
              setFilterValue={tableState.setFilterMenuValue}
              uniqueValues={
                tableState.filterMenuColumnKey
                  ? tableState.uniqueValuesByColumn[
                      tableState.filterMenuColumnKey
                    ] || []
                  : []
              }
              filters={tableState.filters}
              onClose={tableState.closeColumnFilterMenu}
              onApplyFilter={tableState.handleApplyColumnFilter}
              onSelectValue={tableState.handleSelectColumnValue}
              onClearColumnFilters={tableState.handleClearColumnFilters}
            />

            <TablePagination
              page={tableState.page}
              setPage={tableState.setPage}
              rowsPerPage={tableState.rowsPerPage}
              totalRecords={tableState.totalRecords}
              pageStart={tableState.pageStart}
              pageEnd={tableState.pageEnd}
            />
          </Paper>
        )}

        {/* Cancel Button */}
        <Box
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid #dee2e6",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            startIcon={<FeatherIcon icon="x" size={16} />}
          >
            {t("paygap.employeeData.button.cancel")}
          </Button>
        </Box>
      </Box>

      {/* Dialogs */}
      <UpdateConfirmDialog
        open={showUpdateConfirmation}
        onConfirm={handleUpdateDatabaseConfirm}
        onCancel={() => setShowUpdateConfirmation(false)}
      />

      <SuccessDialog
        open={showUpdateSuccess}
        onClose={() => setShowUpdateSuccess(false)}
      />

      <FetchConfirmDialog
        open={showFetchConfirmation}
        hasManuallyAddedRows={hasManuallyAddedRows}
        rowCount={localPersonnelData.length}
        onConfirm={handleFetchDataConfirm}
        onCancel={() => setShowFetchConfirmation(false)}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirmation}
        employeeKey={employeeToDelete?.key || null}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setEmployeeToDelete(null);
        }}
      />

      <ColumnSettingsDialog
        open={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onToggleColumn={handleToggleColumn}
        onShowAll={handleShowAllColumns}
        onHideAll={handleHideAllColumns}
      />
    </Drawer>
  );
};
